/**
 * A MARCA DA LOJA nao assina papel de prestador.
 *
 * Pedido do Ruan em 25/08/2026, olhando um orcamento do AppCel: *"Tenho receio que o
 * usuario faca orcamento em nosso nome e envie para clientes"*. O PDF do prestador
 * ([DA-565]) poe a assinatura dele no topo em corpo 20 e deixa "Fio de Cobre" so num
 * rodape discreto — entao um prestador que se assine `FIO DE COBRE` produz, no
 * celular dele, um papel **indistinguivel de um orcamento nosso**, com preco e prazo
 * que a loja nunca viu, e manda pro cliente final.
 *
 * Vive aqui, em `@fiodecobre/shared`, pelo mesmo motivo do `apagar-mensagem`: as duas
 * pontas precisam da MESMA resposta por motivos diferentes — a **tela** avisa na hora
 * (senao o botao fica cinza e mudo, que e' beco sem saida), o **servidor** recusa de
 * verdade. Duas copias divergiriam no primeiro ajuste, e a que some e' sempre a do
 * backend, que e' a que protege.
 *
 * ## Sao DOIS campos, nao um
 *
 * ⛔ **Bloquear so a Fantasia nao fecha nada.** Quando ela esta vazia o PDF cai de
 * volta no `nome` digitado no cadastro (`orcamento-pdf.ts`: `fantasia || prestador`),
 * entao bastaria escrever `FIO DE COBRE` em *"Seu nome"* e nunca abrir Configuracoes.
 * Medido em prod no dia da decisao: **3 orcamentos ja tinham saido assinados com a
 * marca da loja por essa porta** (CRISTINA FIODECOBRE, GABRYELA FIODECOBRE, JOAO
 * PEDRO FIODECOBRE — todos contas da equipe, entao sem estrago, mas prova de que o
 * caminho funciona). A regra vale nos dois campos, e por isso mora numa peca so.
 *
 * ## Por que achatar antes de comparar
 *
 * A comparacao ignora caixa, acento, espaco e pontuacao — `FIODECOBRE`,
 * `Fio de Cobre`, `FIO-DE-COBRE` e `fío  de  côbre` caem todos no mesmo balde. Sem
 * isso a trava seria enfeite: qualquer hifen a mais passaria.
 *
 * ⛔ **Nao reusar o `gerarSlug`**, que faz quase isto: ele **corta em 80 caracteres**,
 * e a Fantasia aceita 120 — um nome comprido com a marca no fim passaria batido.
 *
 * ⚠️ **O que NAO e' bloqueado, de proposito**: a palavra `COBRE` sozinha. Um
 * eletricista chamado "COBRE & CIA" e' cliente legitimo, e barrar o nome dele pra
 * proteger o nosso seria cobrar dele um problema que e' meu. So a sequencia inteira
 * `fiodecobre` recusa.
 *
 * ⚠️ **A LOGO segue aberta** — quem subir a imagem da loga em ☰ -> Configuracoes
 * monta o mesmo papel sem escrever uma letra. Nao ha como decidir isso por texto;
 * fica registrado em `pendencias.md` como decisao pendente dele.
 */

/** Os sinais combinantes do Unicode (U+0300..U+036F), que o `normalize('NFD')` separa
 *  da letra. Montado por escape, e nao escrito a mao: assim o fonte nao depende de o
 *  arquivo continuar sendo lido como UTF-8. Mesma tecnica do `orcamento-pdf.ts`. */
const RE_ACENTO = new RegExp(`[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`, 'g');

/** A marca, ja achatada — a forma em que a comparacao acontece. */
const MARCA_ACHATADA = 'fiodecobre';

/**
 * Reduz um nome a letras e digitos minusculos, sem acento nem separador.
 *
 * `"Fío de Côbre  Instalações"` -> `"fiodecobreinstalacoes"`.
 */
function achatar(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(RE_ACENTO, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * O nome se apropria da marca da loja?
 *
 * Compara por CONTEM, nao por igualdade: `FIODECOBRE INSTALACOES` e
 * `INSTALACOES FIO DE COBRE` sao os dois casos reais, e nenhum e' igual a marca.
 */
export function usaMarcaDaLoja(valor: string | null | undefined): boolean {
  if (!valor) return false;
  return achatar(valor).includes(MARCA_ACHATADA);
}

/** O que a pessoa le quando digita a marca no campo **Fantasia**. */
export const RECUSA_MARCA_FANTASIA =
  'Este é o nome da loja. Use o nome da sua própria empresa — o orçamento sai assinado por você.';

/** O que a pessoa le quando digita a marca em **Seu nome**, ao criar a conta. */
export const RECUSA_MARCA_NOME =
  'Este é o nome da loja. Escreva o seu nome ou o da sua empresa.';
